import { DateTime } from 'luxon';
import type { UseFormReturn } from 'react-hook-form';
import type { ActorRefFrom } from 'xstate';

import type { ReservationFormFieldRefs } from '../ReservationFormMachinesContext';
import { reservationFormMainMachine } from '../machines/reservationFormMain.machine';
import type {
  ReservationFormInput,
  ReservationFormValues,
} from '../schema/reservationForm.schema';

function toIsoDateTimeString(value: unknown): string {
  if (value instanceof DateTime && value.isValid) {
    return value.toISO() ?? '';
  }
  return '';
}

export type MeasurementsTouched = { height: boolean; weight: boolean };

export type SyncReservationFormMachinesOptions = {
  /**
   * Legs before this index keep their level-of-care machine state. Reseeding
   * it would reset (untouched) or re-derive (touched) their measurement-based
   * gurney limits and attribute level 2.
   */
  levelOfCareFromIndex?: number;
  /** Per-leg touched height/weight. Untouched when omitted (hydration). */
  measurementsTouched?: ReadonlyArray<MeasurementsTouched>;
};

/** Seeds field-machine actors from RHF values (edit/add trip-leg dialogs). */
export function syncReservationFormMachinesFromValues(
  mainActor: ActorRefFrom<typeof reservationFormMainMachine>,
  fieldRefs: ReservationFormFieldRefs,
  values: ReservationFormInput,
  options: SyncReservationFormMachinesOptions = {},
): void {
  const { levelOfCareFromIndex = 0, measurementsTouched } = options;
  const trips = values.trips ?? [];
  const tripCount = Math.max(trips.length, 1);

  // Hydrated or reseeded form: let the next /validate go out even if the
  // values match the last payload sent.
  mainActor.send({ type: 'CLEAR_LAST_FIELD_EDITED_VALIDATION_KEY' });
  mainActor.send({ type: 'SET_TRIP_COUNT', count: tripCount });
  fieldRefs.tripType.send({ type: 'CHANGE', value: values.tripType });

  trips.forEach((trip, index) => {
    fieldRefs.reasonForTrip.send({
      type: 'CHANGE',
      index,
      value: trip.reasonForTrip ?? '',
    });
    fieldRefs.willCall.send({
      type: 'CHANGE',
      index,
      value: Boolean(trip.isWillCall),
    });
    fieldRefs.appointmentDate.send({
      type: 'CHANGE',
      index,
      value: trip.appointmentDate ?? '',
    });
    fieldRefs.appointmentDateTime.send({
      type: 'CHANGE',
      index,
      value: toIsoDateTimeString(trip.appointmentDateTime),
    });
    fieldRefs.pickupDate.send({
      type: 'CHANGE',
      index,
      value: trip.pickupDate ?? '',
    });
    fieldRefs.pickupDateTime.send({
      type: 'CHANGE',
      index,
      value: toIsoDateTimeString(trip.pickupDateTime),
    });
    fieldRefs.pickupAddress.send({
      type: 'CHANGE',
      index,
      value: trip.pickupAddress ?? '',
    });
    fieldRefs.dropoffAddress.send({
      type: 'CHANGE',
      index,
      value: trip.dropoffAddress ?? '',
    });
    fieldRefs.pickupPhone.send({
      type: 'CHANGE',
      index,
      value: trip.pickupPhone ?? '',
    });
    fieldRefs.dropoffPhone.send({
      type: 'CHANGE',
      index,
      value: trip.dropoffPhone ?? '',
    });
    fieldRefs.pickupNotes.send({
      type: 'CHANGE',
      index,
      value: trip.pickupNotes ?? '',
    });
    fieldRefs.dropoffNotes.send({
      type: 'CHANGE',
      index,
      value: trip.dropoffNotes ?? '',
    });
    fieldRefs.passengerMobility.send({
      type: 'CHANGE',
      index,
      value: trip.passengerMobility ?? '',
    });
    fieldRefs.passengerAssistance.send({
      type: 'CHANGE',
      index,
      value: trip.passengerAssistance ?? trip.MobilityAttribute ?? '',
    });
    fieldRefs.standingOrder.send({
      type: 'CHANGE',
      index,
      value: Boolean(trip.standingOrder),
    });

    if (index < levelOfCareFromIndex) return;
    const levelOfCareActor = fieldRefs.levelOfCare;
    for (let level = 1; level <= 6; level++) {
      const key = `AttributeLevel${level}` as keyof typeof trip;
      levelOfCareActor.send({
        type: 'ATTRIBUTE_LEVEL_CHANGE',
        index,
        level: level as 1 | 2 | 3 | 4 | 5 | 6,
        value: String(trip[key] ?? ''),
      });
    }
    levelOfCareActor.send({
      type: 'MOBILITY_ATTRIBUTE_CHANGE',
      index,
      value: trip.MobilityAttribute ?? '',
    });
    levelOfCareActor.send({
      type: 'MEASUREMENTS_CHANGE',
      index,
      height: String(trip.height ?? ''),
      weight: String(trip.weight ?? ''),
      heightTouched: measurementsTouched?.[index]?.height ?? false,
      weightTouched: measurementsTouched?.[index]?.weight ?? false,
    });
  });

  mainActor.send({
    type: 'TRIP_LEG_MERGED_VALUES',
    mergedValues: values as ReservationFormValues,
  });
}

/**
 * Reseeds the field machines after `remove(removedIndex)` on the trips field
 * array. SET_TRIP_COUNT only truncates the machines, so legs from the removed
 * index on would otherwise hold the deleted leg's values. Legs before it are
 * unchanged, so their level of care is left alone; shifted legs get the same
 * level-of-care events LevelOfCareSection sends on an index change, with their
 * touched measurements (RHF shifts touched state on remove because
 * LevelOfCareSection subscribes to touchedFields).
 */
export function syncReservationFormMachinesAfterTripLegRemoved(
  mainActor: ActorRefFrom<typeof reservationFormMainMachine>,
  fieldRefs: ReservationFormFieldRefs,
  form: Pick<
    UseFormReturn<ReservationFormInput>,
    'getValues' | 'getFieldState'
  >,
  removedIndex: number,
): void {
  const values = form.getValues();
  syncReservationFormMachinesFromValues(mainActor, fieldRefs, values, {
    levelOfCareFromIndex: removedIndex,
    measurementsTouched: (values.trips ?? []).map((_, index) => ({
      height: form.getFieldState(`trips.${index}.height`).isTouched,
      weight: form.getFieldState(`trips.${index}.weight`).isTouched,
    })),
  });
}
